import type { Listing } from '@shared/types';
import type { FieldReview, WpImportProperty } from './types';

const PRICE_KEYS = [
  'price',
  '_price',
  'nightly_rate',
  'nightly_price',
  'accommodation_price',
  'rental_price',
  'cost',
];
const BED_KEYS = ['bedrooms', 'bedroom', 'beds', 'beds_number'];
const BATH_KEYS = ['bathrooms', 'bathroom', 'baths'];
const GUEST_KEYS = ['guests', 'guest', 'capacity', 'sleeps', 'max_guests'];
const CITY_KEYS = ['city', 'location_city', 'town'];
const ADDR_KEYS = ['address', 'street', 'location', 'full_address'];
const LAT_KEYS = ['lat', 'latitude', 'map_lat'];
const LNG_KEYS = ['lng', 'longitude', 'lon', 'map_lng'];
const ICA_KEYS = ['unique_code_ica', 'unique_code', 'ica_code', 'property_code'];

function numFromMeta(meta: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = meta[k];
    if (v == null) continue;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    const s = String(v).replace(/,/g, '').trim();
    const n = parseFloat(s);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function strFromMeta(meta: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = meta[k];
    if (v == null) continue;
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && !Number.isNaN(v)) return String(v);
  }
  return '';
}

function boolFromMeta(meta: Record<string, unknown>, keys: string[]): boolean | undefined {
  for (const k of keys) {
    const v = meta[k];
    if (v == null) continue;
    if (typeof v === 'boolean') return v;
    const s = String(v).trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on'].includes(s)) return true;
    if (['false', '0', 'no', 'n', 'off'].includes(s)) return false;
  }
  return undefined;
}

function parseTags(raw: string): string[] {
  return raw
    .split(/[;,|]+/g)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** WP / WooCommerce exports usually store nightly price in Rands (decimal allowed). */
function toCents(raw: number | undefined): number {
  if (raw == null || Number.isNaN(raw) || raw <= 0) return 0;
  return Math.round(raw * 100);
}

export interface ListingDraft {
  docId: string;
  listing: Omit<Listing, 'id' | 'createdAt' | 'updatedAt'>;
  reviews: FieldReview[];
}

export function propertyToListingDraft(
  p: WpImportProperty,
  hostId: string,
  opts: { skipContent: boolean }
): ListingDraft {
  const reviews: FieldReview[] = [...p.reviews];
  const meta = p.meta;
  const isWpId = /^\d+$/.test(String(p.wpPostId ?? '').trim());

  const priceRaw = numFromMeta(meta, PRICE_KEYS);
  const basePrice = toCents(priceRaw);
  if (!priceRaw) {
    reviews.push({
      field: 'basePrice',
      message: 'No price meta found; defaulting to 0 (set in editor after import)',
      severity: 'warn',
    });
  }

  const bedrooms = Math.max(1, Math.round(numFromMeta(meta, BED_KEYS) ?? 1));
  const bathrooms = Math.max(1, Math.round(numFromMeta(meta, BATH_KEYS) ?? 1));
  const capacity = Math.max(
    1,
    Math.round(numFromMeta(meta, GUEST_KEYS) ?? bedrooms * 2)
  );

  const city = strFromMeta(meta, CITY_KEYS) || '';
  const address = strFromMeta(meta, ADDR_KEYS) || city || '';
  const lat = numFromMeta(meta, LAT_KEYS) ?? 0;
  const lng = numFromMeta(meta, LNG_KEYS) ?? 0;
  if (!lat || !lng) {
    reviews.push({
      field: 'location',
      message: 'Lat/lng missing or zero; set on map in listing editor',
      severity: 'warn',
    });
  }

  const uniqueCodeIca = strFromMeta(meta, ICA_KEYS) || null;
  const description = opts.skipContent
    ? p.postContent
    : p.postContentHtml || p.postContent;

  const slugBase = p.postTitle
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);

  const region = strFromMeta(meta, ['region', 'province']);
  const country = strFromMeta(meta, ['country']) || 'South Africa';

  const statusRaw = strFromMeta(meta, ['status']).trim().toLowerCase();
  const status =
    statusRaw === 'published' || statusRaw === 'draft' || statusRaw === 'archived'
      ? (statusRaw as Listing['status'])
      : 'draft';
  const featured = boolFromMeta(meta, ['featured', 'is_featured']) ?? false;
  const extraTags = parseTags(strFromMeta(meta, ['tags']));

  const listing: Omit<Listing, 'id' | 'createdAt' | 'updatedAt'> = {
    title: p.postTitle,
    ...(slugBase ? { slug: slugBase } : {}),
    description,
    hostId,
    location: {
      lat,
      lng,
      address,
      city,
      country,
      ...(region ? { region } : {}),
    },
    images: [],
    basePrice,
    priceRules: [],
    minNights: Math.max(1, Math.round(numFromMeta(meta, ['min_nights', 'minimum_stay']) ?? 1)),
    maxNights: Math.max(
      1,
      Math.round(numFromMeta(meta, ['max_nights', 'maximum_stay']) ?? 30)
    ),
    capacity,
    bedrooms,
    bathrooms,
    amenities: [],
    tags: [...new Set([isWpId ? 'wp-import' : 'csv-import', ...extraTags])],
    status,
    featured,
    importSource: isWpId ? 'wp_rentals_csv' : 'csv_import',
    ...(isWpId ? { wpPostId: p.wpPostId } : {}),
    ...(uniqueCodeIca ? { uniqueCodeIca } : {}),
  };

  const stableBase = (p.wpPostId || slugBase || 'listing').replace(/[^a-zA-Z0-9_-]/g, '_');
  const docId = `${isWpId ? 'wp' : 'csv'}-${stableBase}`.slice(0, 120);

  return { docId, listing, reviews };
}
